import { getColor } from "@/lib/methodUtils";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { memo } from "react";
import { ButtonLoader } from "../Loader/ButtonLoader";

interface ModalProps {
  modalOpen: boolean;
  closeModal: () => void;
  heading: string;
  children: React.ReactNode;
  width?: string;
  saveButtonText?: string;
  onSave?: () => void;
  errorMessage?: string;
  smallHeading?: string;
  hideFooter?: boolean;
  hideAddButton?: boolean;
  isDisabled?: boolean;
  isColorBadge?: boolean;
  color?: string;
  onNoClickText?: string;
  showGreenButton?: boolean;
  greenText?: string;
  onGreen?: () => void;
  showRedButton?: boolean;
  redText?: string;
  onRed?: () => void;
  rightHeadingText?: string;
  isLoading?: boolean;
  contentSx?: object;
  cancelButtonText?:string;
  height?:string;
  hideCancelButton?:boolean;
  isSendEmail?:boolean;
  SendEmail? :() => void;
  SendEmailText?:string
  isEmailLoading?:boolean; 
  isRightHeadingButton?:boolean;  
  rightHeadingButtonText?:string;
  onRightHeadingButtonClick?: () => void;
  timeError?: string;
  subHeading?: string;
  hideHeading?:boolean;
  dense?: boolean;
  isCloseClicked?: boolean;
  isButtonVariant? : boolean;
  smallHeadingCSS?: string;
}

const Modal: React.FC<ModalProps> = ({
  modalOpen,
  closeModal,
  heading,
  children,
  width,
  saveButtonText = "Save",
  onSave,
  errorMessage,
  smallHeading,
  hideFooter,
  hideAddButton,
  isDisabled,
  isColorBadge,
  color,
  onNoClickText,
  height,
  hideCancelButton,
  showGreenButton,
  greenText,
  onGreen,
  showRedButton,
  redText,
  onRed,
  isLoading = false,
  rightHeadingText,
  contentSx,
  isSendEmail,
  SendEmail,
  SendEmailText="Send Email",
  isEmailLoading=false,
  isRightHeadingButton =false,
  rightHeadingButtonText,
  onRightHeadingButtonClick,
  timeError,
  subHeading,
  hideHeading=false,
  dense = false,
  isCloseClicked = true,
  isButtonVariant = false,
  smallHeadingCSS,
}: ModalProps) => {
  return (
    <Dialog
      open={modalOpen}
      onClose={isCloseClicked ? closeModal : undefined}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          style: { width: width || "40vw", height: height || "auto", maxWidth: "70vw", maxHeight: "80vh" },
        },
      }}
    >
      {!hideHeading && ( 
        <DialogTitle 
        variant={dense ? "h6" : "h5"}
        sx={dense ? {
          py: 1.5,
          px: 3,
          fontWeight: 500,
          borderBottom: "1px solid #e0e0e0",
          color: "#5f6368"
        } : {}}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {heading} <small className={smallHeadingCSS}>{smallHeading}</small>
            <div className="text-gray-500 font-sm">
              <small>{subHeading}</small>
            </div>
          </div>
          <small>{rightHeadingText}</small>
          {isRightHeadingButton && (
            <Button 
              variant={isButtonVariant ? "text" : "contained"} 
              size={dense ? "small" : "medium"}
              onClick={onRightHeadingButtonClick}
            >
              {rightHeadingButtonText ? rightHeadingButtonText : "Add More"}
            </Button>
          )}
        </div>
        {isColorBadge && (
          <div className="absolute top-0 right-0 w-7 h-7" style={{ backgroundColor: getColor(color) }} />
        )}
      </DialogTitle>
      )}
      <DialogContent
        dividers
        sx={{ ...contentSx }}>
        {children} {/* content comes first */}
        {errorMessage && (
          <Typography color="error" mt={2}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      {!hideFooter && <DialogActions>
        {/* show error */}
        {timeError && (
          <p className="text-red-500 text-sm my-4">
            {timeError}
          </p>
        )}

        {showGreenButton &&
          <Button
            style={{backgroundColor: "#5aac44", color: "#fff", textTransform: dense ? "none" : "none" }}
            variant="contained"
            size={dense ? "small" : "medium"}
            onClick={onGreen || closeModal}
          >
            {greenText || "Accept"}
          </Button>
        }
        {showRedButton &&
          <Button
            style={{backgroundColor: "#fd397a", color: "#fff", textTransform: dense ? "none" : "none" }}
            variant="contained"
            size={dense ? "small" : "medium"}
            onClick={onRed || closeModal}
          >
            {redText || "Reject"}
          </Button>
        }
        {!hideCancelButton &&
          <Button
            style={{ 
              backgroundColor: "#fff", 
              color: "#3c4043",
              textTransform: dense ? "none" : "none",
              border: dense ? "1px solid #e0e0e0" : "none",
              boxShadow: dense ? "0px 2px 4px rgba(0,0,0,0.05)" : "none"
            }}
            variant="contained"
            size={dense ? "small" : "medium"}
            onClick={closeModal}
          >
            {onNoClickText || "Cancel"}
          </Button>
        }
        {!hideAddButton && <ButtonLoader
          loading={isLoading}
          style={{
            textTransform: dense ? "none" : "none",
          }}
          variant="contained"
          size={dense ? "small" : "medium"}
          disabled={isDisabled ? true : false}
          onClick={onSave || closeModal}

        >
          {saveButtonText}
        </ButtonLoader>}
        {isSendEmail  && (
          <ButtonLoader
            loading={isEmailLoading}
            style={{ marginLeft: 8, textTransform: dense ? "none" : "none" }}
            variant="outlined"
            size={dense ? "small" : "medium"}
            onClick={SendEmail}
            spinnerColor="#808080"
          >
            {SendEmailText || ""}
          </ButtonLoader>
        )}
      </DialogActions>}
    </Dialog>
  );
}

export default memo(Modal);
